import { ReactNode } from "react";
import { DocumentKind } from "@bitory-gmbh/bsm";
import { formatDate, formatTime } from "@/shared/lib/utilities/date";
import {
  ListRequest,
  useHtzWorkOrderListQuery,
  WorkOrderOrderBy,
} from "@/services/backend/htz/work-order/api/work-order-api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import t from "@/lang/lang";
import { BranchName } from "@/services/backend/branch/components/branch-name";
import { PaginationLimitOffset } from "@/shared/components/tables/pagination-limit-offset";
import { TableCard } from "@/shared/components/tables/table-card";
import { DateBetweenFilter } from "@/shared/components/filter/date-between-filter";
import { OrderBySelect } from "@/shared/components/tables/order-by-select";
import { FulfillmentStateFilter } from "@/services/backend/relationships/components/fulfillment-state-filter";
import { BranchFilter } from "@/services/backend/branch/components/branch-filter";
import { FulfillmentStateIcon } from "@/services/backend/relationships/components/fulfillment-state-icon";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";

export function WorkOrderTable({
  request,
  onRequestChange,
  renderRowLink,
}: {
  request: ListRequest;
  onRequestChange: (request: ListRequest) => void;
  renderRowLink: (
    workOrderId: string,
    processId: string,
    documentKind: DocumentKind,
  ) => ReactNode;
}) {
  const { data: list, isLoading, error } = useHtzWorkOrderListQuery(request);

  const emptyList = !isLoading && !error && list!.items.length === 0;

  return (
    <div className="grid gap-2">
      <TableCard>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("Nummer")}</TableHead>
              <TableHead>{t("Status")}</TableHead>
              <TableHead className="whitespace-nowrap">
                {t("Ausführende NL")}
              </TableHead>
              <TableHead>{t("Teamleiter")}</TableHead>
              <TableHead>{t("Beginn")}</TableHead>
              <TableHead>{t("Ende")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <TableLoadingRow colSpan={colCount} />}
            {error && <TableRtkErrorRow error={error} colSpan={colCount} />}
            {emptyList && <TableEmptyRow colSpan={colCount} />}
            {list?.items.map((workOrder) => (
              <TableRow key={workOrder.id}>
                <TableCell>
                  {renderRowLink(
                    workOrder.id,
                    workOrder.processId!,
                    workOrder.documentKind!,
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center">
                    <FulfillmentStateIcon state={workOrder.state} />
                    <span className="ml-2">{t(workOrder.state)}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <BranchName branchId={workOrder.executingBranchId} />
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  {workOrder.teamLeader}
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  {workOrder.beginDate && (
                    <>
                      {formatDate(workOrder.beginDate)}
                      {", "}
                      {formatTime(workOrder.beginDate)}
                    </>
                  )}
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  {workOrder.endDate && (
                    <>
                      {formatDate(workOrder.endDate)}
                      {", "}
                      {formatTime(workOrder.endDate)}
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableCard>
      {list && !emptyList && (
        <PaginationLimitOffset
          offset={request.offset ?? 0}
          limit={request.limit ?? 0}
          totalCount={list.totalCount}
          setLimitOffset={(limit, offset) => {
            onRequestChange({ ...request, limit, offset });
          }}
          className="ml-auto"
        />
      )}
    </div>
  );
}

const colCount = 6;

export function WorkOrderTableFilters({
  request,
  onRequestChange,
  hideStateFilter = false,
  hideBranchFilter = false,
  hideDateFilter = false,
}: {
  request: ListRequest;
  onRequestChange: (request: ListRequest) => void;
  hideStateFilter?: boolean;
  hideBranchFilter?: boolean;
  hideDateFilter?: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <FulfillmentStateFilter
        filter={request.state}
        onFilterChange={(state) => onRequestChange({ ...request, state })}
        hidden={hideStateFilter}
      />
      <BranchFilter
        filter={request.executingBranch}
        onFilterChange={(executingBranch) =>
          onRequestChange({ ...request, executingBranch })
        }
        hidden={hideBranchFilter}
      />
      <DateBetweenFilter
        label={t("Datum")}
        filter={request.date}
        onFilterChange={(date) => onRequestChange({ ...request, date })}
        hidden={hideDateFilter}
      />
      <OrderBySelect<WorkOrderOrderBy>
        orderBy={request.orderBy}
        onOrderByChange={(orderBy) => onRequestChange({ ...request, orderBy })}
        fields={[
          { label: t("Status"), value: "state" },
          { label: t("Beginn"), value: "beginDate" },
          { label: t("Ende"), value: "endDate" },
          { label: t("Teamleiter"), value: "teamLeader" },
          { label: t("Erstellt am"), value: "createdAt" },
          { label: t("Bearbeitet am"), value: "updatedAt" },
        ]}
      />
    </div>
  );
}

export function useDefaultHtzWorkOrderListRequest(
  processId: string,
): ListRequest {
  return {
    process: { active: true, values: [processId] },
    limit: 50,
    offset: 0,
  };
}
